import { useMemo } from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine,
} from 'recharts';
import { JournalSidebar } from './JournalSidebar';
import { EnhancedStats } from './EnhancedStats';
import { useAuthoritativeTradesRecords } from '../../hooks/useAuthoritativeTrades';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';
import {
    filterTradesByDateKey,
    getTradePnl,
    getTradeTimestampMs,
    getTradeVolume,
    normalizeTradeSide,
    normalizeTradeStatus,
} from '../../lib/trades/authoritativeTradeData';

// Types
type Trade = {
    id: number;
    date: string;
    symbol: string;
    volume: number;
    executions: number;
    pnl: number;
    side: 'Long' | 'Short';
    status: 'open' | 'closed';
    timestampMs?: number | null;
};

// Constants
const GREEN = '#10b981';
const RED = '#ef4444';
const ZINC = '#71717a';

// Utilities
const fmtMoney = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    signDisplay: 'auto',
  }).format(amount);
};

const fmtAbbreviate = (n: number) => {
    const sign = n < 0 ? '-' : '';
    const num = Math.abs(n);
    if (num >= 1e6) return `${sign}$${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${sign}$${(num / 1e3).toFixed(0)}K`;
    return fmtMoney(n);
};

const toDateKey = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Custom Tooltip Component
const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm p-3 shadow-lg text-sm">
          <p className="font-bold text-zinc-800 dark:text-zinc-200">Trade {data.tradeNumber}</p>
          <p className="text-zinc-600 dark:text-zinc-400">
            P&L: 
            <span className={`font-medium ${data.pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {' '}{fmtMoney(data.pnl)}
            </span>
          </p>
          <p className="text-zinc-600 dark:text-zinc-400">
            Cumulative: 
            <span className={`font-medium ${data.cumulativePnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {' '}{fmtMoney(data.cumulativePnl)}
            </span>
          </p>
        </div>
      );
    }
    return null;
};

// Main Component
interface DayJournalPageProps {
    date: Date;
    onBack: () => void;
    onNavigateDate?: (newDate: Date) => void;
}

export function DayJournalPage({ date, onBack, onNavigateDate }: DayJournalPageProps) {
    const displayTimeZone = useDisplayTimezone();
    const authoritativeTrades = useAuthoritativeTradesRecords();
    const dateKey = useMemo(() => toDateKey(date), [date]);
    const dayTrades = useMemo<Trade[]>(() => {
        return filterTradesByDateKey(authoritativeTrades as any[], dateKey).map((trade: any, index: number) => {
            const numericId = Number(trade?.id ?? trade?.timestamp ?? trade?.openTimestampMs ?? index + 1);
            const timestampMs = getTradeTimestampMs(trade);
            return {
                id: Number.isFinite(numericId) ? numericId : index + 1,
                date: dateKey,
                symbol: String(trade?.symbol ?? '—'),
                volume: getTradeVolume(trade),
                executions: Number(trade?.executions ?? trade?.executionCount ?? trade?.fills ?? trade?.executionRows?.length ?? 0),
                pnl: getTradePnl(trade),
                side: normalizeTradeSide(trade) === 'SHORT' ? 'Short' : 'Long',
                status: normalizeTradeStatus(trade),
                timestampMs,
            };
        });
    }, [authoritativeTrades, dateKey]);
    const dailyPnl = dayTrades.reduce((acc, t) => acc + t.pnl, 0);
    const orderedTrades = useMemo(() => [...dayTrades].sort((left, right) => (left.timestampMs ?? 0) - (right.timestampMs ?? 0)), [dayTrades]);

    const chartData = useMemo(() => {
        let cumulativePnl = 0;
        return orderedTrades.map((trade, index) => {
            cumulativePnl += trade.pnl;
            return {
                tradeNumber: index + 1,
                pnl: trade.pnl,
                cumulativePnl
            };
        });
    }, [orderedTrades]);
    
    const navigateDay = (direction: 'prev' | 'next') => {
        if (onNavigateDate) {
            const newDate = new Date(date);
            newDate.setDate(newDate.getDate() + (direction === 'prev' ? -1 : 1));
            onNavigateDate(newDate);
        }
    }

    return (
        <div className="flex h-full">
            {/* Main Content */}
            <div className="flex-1 space-y-6 pr-4">
                <div className="flex justify-between items-center">
                    <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                        Back to Calendar
                    </button>
                </div>

                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <h1 className="text-2xl font-bold text-zinc-800 dark:text-zinc-200">
                            {date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric', timeZone: displayTimeZone })}
                        </h1>
                        {onNavigateDate && (
                            <div className="flex items-center gap-2">
                                <button onClick={() => navigateDay('prev')} className="px-3 py-1.5 text-sm flex items-center gap-2 rounded-md bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700">
                                   <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg> Previous Day
                                </button>
                                <button onClick={() => navigateDay('next')} className="px-3 py-1.5 text-sm flex items-center gap-2 rounded-md bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700">
                                    Next Day <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                                </button>
                            </div>
                        )}
                    </div>
                    <button className="px-4 h-9 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700">Create New Entry</button>
                </div>

                <div className="space-y-6">
                    <div className="rounded-lg bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4 h-64 flex flex-col">
                        <h4 className="text-sm font-semibold text-zinc-600 dark:text-zinc-400 text-center mb-1">Cumulative P&L by Trade</h4>
                        {dayTrades.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                 <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                                     <defs>
                                         <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                                             <stop offset="5%" stopColor={GREEN} stopOpacity={0.8}/>
                                             <stop offset="95%" stopColor={GREEN} stopOpacity={0}/>
                                         </linearGradient>
                                         <linearGradient id="colorNegative" x1="0" y1="0" x2="0" y2="1">
                                             <stop offset="5%" stopColor={RED} stopOpacity={0.8}/>
                                             <stop offset="95%" stopColor={RED} stopOpacity={0}/>
                                         </linearGradient>
                                     </defs>
                                     <XAxis 
                                         dataKey="tradeNumber" 
                                         stroke={ZINC}
                                         tick={{ fontSize: 10, fill: ZINC }}
                                         label={{ value: 'Trades', position: 'insideBottom', dy: 10, fill: ZINC, fontSize: 12, fontWeight: '600' }}
                                         interval={0}
                                         axisLine={{ stroke: ZINC }}
                                         tickLine={{ stroke: ZINC }}
                                     />
                                     <YAxis 
                                         stroke={ZINC} 
                                         tickFormatter={fmtAbbreviate}
                                         tick={{ fontSize: 10, fill: ZINC }}
                                         label={{ value: 'Profit', angle: -90, position: 'insideLeft', dx: -10, fill: ZINC, fontSize: 12, fontWeight: '600' }}
                                         axisLine={{ stroke: ZINC }}
                                         tickLine={{ stroke: ZINC }}
                                         domain={[(dataMin: number) => Math.min(dataMin, 0), 'dataMax']}
                                         width={40}
                                     />
                                     <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.15)"/>
                                     <Tooltip content={<CustomTooltip />} />
                                     <ReferenceLine y={0} stroke={ZINC} strokeDasharray="3 3" />
                                     <Area type="monotone" dataKey="cumulativePnl" stroke={dailyPnl >= 0 ? GREEN : RED} fillOpacity={1} fill={dailyPnl >= 0 ? "url(#colorPositive)" : "url(#colorNegative)"} />
                                 </AreaChart>
                             </ResponsiveContainer>
                        ) : (
                            <div className="flex h-full items-center justify-center text-sm text-zinc-500 dark:text-zinc-400">
                                No trade data yet.
                            </div>
                        )}
                    </div>
                    <div>
                        <EnhancedStats trades={dayTrades} totalPnl={dailyPnl} />
                    </div>
                </div>
                
                <div>
                     <textarea placeholder="Click here to start typing your notes..." className="w-full h-24 p-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                     <div className="text-right mt-2">
                         <button className="px-4 py-1.5 text-sm rounded-md bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700">Customize</button>
                     </div>
                 </div>

                {dayTrades.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-50 dark:bg-zinc-800/50">
                                <tr>
                                    {['Status', 'Symbol', 'Volume', 'Execs', 'P&L', 'Shared', 'Notes', 'Tags'].map(h =>
                                        <th key={h} className="px-4 py-2 text-left font-medium text-zinc-500 dark:text-zinc-400">{h}</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-zinc-900">
                                {dayTrades.map(trade => (
                                    <tr key={trade.id} className="border-t border-zinc-100 dark:border-zinc-800">
                                        <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400">{trade.status}</td>
                                        <td className="px-4 py-3 font-semibold">{trade.symbol}</td>
                                        <td className="px-4 py-3">{trade.volume}</td>
                                        <td className="px-4 py-3">{trade.executions}</td>
                                        <td className={`px-4 py-3 font-semibold ${trade.pnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtMoney(trade.pnl)}</td>
                                        <td className="px-4 py-3"></td>
                                        <td className="px-4 py-3"></td>
                                        <td className="px-4 py-3"></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {/* Sidebar */}
            <JournalSidebar />
        </div>
    );
}
